"use client";
import {
  AbsoluteFill,
  Sequence,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";

// ════════════════════════════════════════════════════════════════════
// COCA-COLA OVERLAYS — transparent, word-stamp synced.
// 16fps · 1280x704 · base video 640.35s.
// Palette: classic Coke red + white + silver + caution-gold.
// ════════════════════════════════════════════════════════════════════

const FPS = 16;
const sToF = (s: number) => Math.round(s * FPS);
const FONT = "Inter, system-ui, sans-serif";
const TS = "0 3px 14px rgba(0,0,0,0.92), 0 1px 5px rgba(0,0,0,0.95), 0 0 2px rgba(0,0,0,0.9)";

const COKE_RED = "#E61A27";
const WHITE = "#ffffff";
const CREAM = "#f5f0e8";
const SILVER = "#c0c4cc";
const GOLD = "#d4a574";
const DIM = "#8a8f99";

const appear = (t: number, atS: number, dur = 0.18) =>
  interpolate(t, [atS, atS + dur], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

const win = (t: number, inS: number, outS: number) =>
  interpolate(t, [inS, inS + 0.2, outS - 0.4, outS], [0, 1, 1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

// ─── 1 · STAMP — rotated framed callout ───────────────────────────────
const Stamp: React.FC<{ s: number; title: string; sub?: string; atS: number; outS: number; color?: string; rotate?: number }> = ({ s, title, sub, atS, outS, color = COKE_RED, rotate = -8 }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = s + frame / FPS;
  const op = win(t, atS, outS);
  const sc = spring({ frame: Math.max(0, frame - sToF(atS - s)), fps, config: { damping: 9, stiffness: 130 } });
  const rot = interpolate(t, [atS, atS + 0.35], [rotate - 6, rotate], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ transform: `rotate(${rot}deg) scale(${sc})`, border: `4px solid ${color}`, padding: "22px 48px", color, boxShadow: `0 0 28px ${color}55, 0 8px 26px rgba(0,0,0,0.7)`, background: "rgba(0,0,0,0.45)" }}>
        <div style={{ fontSize: 50, fontWeight: 900, letterSpacing: "0.07em", textShadow: TS }}>{title}</div>
        {sub && <div style={{ fontSize: 18, marginTop: 7, letterSpacing: "0.2em", textAlign: "center", textShadow: TS, color: WHITE }}>{sub}</div>}
      </div>
    </AbsoluteFill>
  );
};

// ─── 2 · TEXT HIGHLIGHT — keyword punches in on its word ──────────────
const TextHighlight: React.FC<{ s: number; pre: string; hi: string; post: string; hiS: number; outS: number; hiColor?: string }> = ({ s, pre, hi, post, hiS, outS, hiColor = COKE_RED }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const preS = hiS - 0.3;
  const op = win(t, preS, outS);
  const hiOp = appear(t, hiS, 0.14);
  const hiSc = interpolate(t, [hiS, hiS + 0.25], [0.78, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ textAlign: "center", maxWidth: 1100 }}>
        {pre && <span style={{ color: DIM, fontSize: 36, fontWeight: 500, textShadow: TS, opacity: appear(t, preS, 0.2) }}>{pre} </span>}
        <span style={{ color: hiColor, fontSize: 64, fontWeight: 900, letterSpacing: "0.04em", textShadow: `0 0 22px ${hiColor}77, ${TS}`, opacity: hiOp, display: "inline-block", transform: `scale(${hiSc})` }}>{hi}</span>
        {post && <span style={{ color: DIM, fontSize: 36, fontWeight: 500, textShadow: TS, opacity: appear(t, hiS + 0.25, 0.2) }}> {post}</span>}
      </div>
    </AbsoluteFill>
  );
};

// ─── 3 · HOOK TEXT — single punchy line ───────────────────────────────
const HookText: React.FC<{ s: number; text: string; atS: number; outS: number }> = ({ s, text, atS, outS }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const op = win(t, atS, outS);
  const sc = interpolate(t, [atS, atS + 0.3], [0.9, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ color: WHITE, fontSize: 64, fontWeight: 900, letterSpacing: "0.04em", textShadow: TS, transform: `scale(${sc})`, textAlign: "center", lineHeight: 1.08 }}>
        {text}
      </div>
    </AbsoluteFill>
  );
};

// ─── 4 · LOWER THIRD — bottom-left, with red accent bar ───────────────
const LowerThird: React.FC<{
  s: number; name: string; sub: string; italic?: string; nameS: number; subS: number; outS: number;
}> = ({ s, name, sub, italic, nameS, subS, outS }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const op = win(t, nameS - 0.2, outS);
  const slideX = interpolate(t, [nameS - 0.25, nameS + 0.15], [-260, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: (v) => 1 - Math.pow(1 - v, 3),
  });
  const nameOp = appear(t, nameS, 0.16);
  const subOp = appear(t, subS, 0.2);
  const accentW = interpolate(t, [nameS - 0.2, nameS + 0.35], [0, 168], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ opacity: op }}>
      <div style={{ position: "absolute", bottom: 86, left: 64, transform: `translateX(${slideX}px)`, fontFamily: FONT }}>
        <div style={{ height: 3, width: accentW, background: COKE_RED, marginBottom: 10, boxShadow: `0 0 10px ${COKE_RED}99` }} />
        <div style={{ color: WHITE, fontSize: 34, fontWeight: 800, letterSpacing: "-0.4px", textShadow: TS, lineHeight: 1.05, opacity: nameOp }}>{name}</div>
        <div style={{ color: COKE_RED, fontSize: 16, fontWeight: 700, letterSpacing: "0.18em", marginTop: 6, textShadow: TS, opacity: subOp }}>{sub}</div>
        {italic && <div style={{ color: SILVER, fontSize: 13, fontStyle: "italic", marginTop: 4, textShadow: TS, opacity: subOp }}>{italic}</div>}
      </div>
    </AbsoluteFill>
  );
};

// ─── 5 · BIG STAT — number counts up and locks on the value word ──────
const BigStat: React.FC<{
  s: number; label: string; value: number; valueS: number; outS: number;
  decimals?: number; prefix?: string; suffix?: string; sub?: string; color?: string;
}> = ({ s, label, value, valueS, outS, decimals = 0, prefix, suffix, sub, color = WHITE }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = s + frame / FPS;
  const labelS = valueS - 0.25;
  const op = win(t, labelS, outS);
  const cur = interpolate(t, [valueS, valueS + 0.42], [0, value], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const numOp = appear(t, valueS, 0.12);
  const sc = spring({ frame: Math.max(0, frame - sToF(valueS - s)), fps, config: { damping: 13, stiffness: 95 } });
  const subOp = appear(t, valueS + 0.5, 0.3);
  const shown = value >= 100 ? Math.round(cur).toLocaleString() : cur.toFixed(decimals);
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ color: COKE_RED, fontSize: 25, letterSpacing: "0.24em", marginBottom: 14, textShadow: TS, opacity: appear(t, labelS, 0.2), fontWeight: 700 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, opacity: numOp, transform: `scale(${sc})` }}>
        {prefix && <span style={{ color, fontSize: 52, fontWeight: 800, textShadow: TS }}>{prefix}</span>}
        <span style={{ color, fontSize: 168, fontWeight: 800, letterSpacing: "-4px", textShadow: TS, lineHeight: 1 }}>{shown}</span>
        {suffix && <span style={{ color: COKE_RED, fontSize: 56, fontWeight: 800, textShadow: TS }}>{suffix}</span>}
      </div>
      {sub && <div style={{ color: CREAM, fontSize: 21, letterSpacing: "0.16em", marginTop: 20, textShadow: TS, opacity: subOp }}>{sub}</div>}
    </AbsoluteFill>
  );
};

// ─── 6 · CHAPTER — PHASE 01..06 / TITLE ───────────────────────────────
const Chapter: React.FC<{ s: number; num: string; title: string; sub?: string; numS: number; titleS: number; outS: number; labelText?: string }> = ({ s, num, title, sub, numS, titleS, outS, labelText = "PHASE" }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = s + frame / FPS;
  const labelS = numS - 0.3;
  const op = win(t, labelS, outS);
  const numScale = spring({ frame: Math.max(0, frame - sToF(numS - s)), fps, config: { damping: 12, stiffness: 85 } });
  const numOp = appear(t, numS, 0.12);
  const lineW = interpolate(t, [titleS - 0.15, titleS + 0.5], [0, 150], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const titleOp = appear(t, titleS, 0.16);
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", flexDirection: "column", opacity: op }}>
      <p style={{ color: COKE_RED, fontSize: 21, fontWeight: 700, letterSpacing: "0.32em", textTransform: "uppercase", margin: 0, marginBottom: 12, textShadow: TS, opacity: appear(t, labelS, 0.2) }}>{labelText}</p>
      <h1 style={{ color: WHITE, fontSize: "9rem", fontWeight: 800, margin: 0, lineHeight: 1, transform: `scale(${numScale})`, textShadow: TS, opacity: numOp }}>{num}</h1>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 20 }}>
        <div style={{ height: 1, width: lineW, backgroundColor: COKE_RED }} />
        <div style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: COKE_RED, opacity: titleOp, boxShadow: `0 0 8px ${COKE_RED}` }} />
        <div style={{ height: 1, width: lineW, backgroundColor: COKE_RED }} />
      </div>
      <h2 style={{ color: CREAM, fontSize: "2.5rem", fontWeight: 800, letterSpacing: "0.05em", margin: 0, marginTop: 12, textShadow: TS, opacity: titleOp }}>{title}</h2>
      {sub && <p style={{ color: SILVER, fontSize: "1.15rem", marginTop: 9, letterSpacing: "0.14em", textShadow: TS, opacity: appear(t, titleS + 0.3, 0.25) }}>{sub}</p>}
    </AbsoluteFill>
  );
};

// ─── 7 · EQUATION STACK — line1 / op / line2 ──────────────────────────
const EquationStack: React.FC<{
  s: number; l1: string; op: string; l2: string; l1S: number; opS: number; l2S: number; outS: number; opColor?: string;
}> = ({ s, l1, op, l2, l1S, opS, l2S, outS, opColor = COKE_RED }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const wOp = win(t, l1S, outS);
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: wOp }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
        <div style={{ color: WHITE, fontSize: 56, fontWeight: 900, letterSpacing: "0.03em", textShadow: TS, opacity: appear(t, l1S, 0.16), transform: `scale(${interpolate(t, [l1S, l1S + 0.25], [0.85, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" })})` }}>{l1}</div>
        <div style={{ color: opColor, fontSize: 46, fontWeight: 900, textShadow: TS, opacity: appear(t, opS, 0.14) }}>{op}</div>
        <div style={{ color: WHITE, fontSize: 56, fontWeight: 900, letterSpacing: "0.03em", textShadow: TS, opacity: appear(t, l2S, 0.16), transform: `scale(${interpolate(t, [l2S, l2S + 0.25], [0.85, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" })})` }}>{l2}</div>
      </div>
    </AbsoluteFill>
  );
};

// ─── 8 · POLLUTER STAMP — special card with crown + ranking ───────────
const PolluterStamp: React.FC<{ s: number; atS: number; outS: number }> = ({ s, atS, outS }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = s + frame / FPS;
  const op = win(t, atS, outS);
  const sc = spring({ frame: Math.max(0, frame - sToF(atS - s)), fps, config: { damping: 9, stiffness: 120 } });
  const rot = interpolate(t, [atS, atS + 0.35], [-14, -8], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const subOp = appear(t, atS + 0.5, 0.25);
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ transform: `rotate(${rot}deg) scale(${sc})`, padding: "22px 60px", color: COKE_RED, border: `5px solid ${COKE_RED}`, background: "rgba(0,0,0,0.5)", boxShadow: `0 0 36px ${COKE_RED}66, 0 8px 28px rgba(0,0,0,0.75)`, textAlign: "center" }}>
        <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: "0.32em", color: WHITE, marginBottom: 6, textShadow: TS }}>WORLD'S</div>
        <div style={{ fontSize: 130, fontWeight: 900, lineHeight: 0.92, letterSpacing: "-4px", textShadow: TS }}>#1</div>
        <div style={{ fontSize: 30, fontWeight: 900, letterSpacing: "0.16em", color: WHITE, marginTop: 6, textShadow: TS }}>PLASTIC POLLUTER</div>
        <div style={{ fontSize: 15, marginTop: 10, letterSpacing: "0.22em", color: COKE_RED, textShadow: TS, opacity: subOp }}>SIXTH YEAR IN A ROW · BFFP AUDIT</div>
      </div>
    </AbsoluteFill>
  );
};

// ─── 9 · WARNING LABEL — Diet Coke PHENYLKETONURICS card ──────────────
const PKUWarning: React.FC<{ s: number; atS: number; outS: number }> = ({ s, atS, outS }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const op = win(t, atS, outS);
  const phOp = appear(t, atS, 0.16);
  const subOp = appear(t, atS + 0.6, 0.22);
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ border: `4px solid ${GOLD}`, padding: "20px 36px", background: "rgba(0,0,0,0.65)", textAlign: "center", maxWidth: 900, boxShadow: `0 0 28px ${GOLD}55` }}>
        <div style={{ color: GOLD, fontSize: 22, fontWeight: 900, letterSpacing: "0.28em", textShadow: TS, opacity: phOp }}>⚠  MANDATORY WARNING</div>
        <div style={{ color: WHITE, fontSize: 44, fontWeight: 900, letterSpacing: "0.04em", marginTop: 14, textShadow: TS, opacity: phOp }}>PHENYLKETONURICS:</div>
        <div style={{ color: WHITE, fontSize: 30, fontWeight: 800, letterSpacing: "0.06em", marginTop: 6, textShadow: TS, opacity: subOp }}>CONTAINS PHENYLALANINE</div>
        <div style={{ color: GOLD, fontSize: 14, marginTop: 14, letterSpacing: "0.22em", textShadow: TS, opacity: subOp }}>21 CFR 172.804 · FEDERAL REGULATION</div>
      </div>
    </AbsoluteFill>
  );
};

// ─── 10 · END CARD ────────────────────────────────────────────────────
const EndCard: React.FC<{ s: number; atS: number; outS: number }> = ({ s, atS, outS }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const op = win(t, atS, outS);
  const subOp = appear(t, atS + 0.6, 0.3);
  const nextOp = appear(t, atS + 1.4, 0.3);
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ color: COKE_RED, fontSize: 26, letterSpacing: "0.32em", marginBottom: 22, textShadow: TS, fontWeight: 800 }}>FACTORY FILES</div>
      <div style={{ color: WHITE, fontSize: 60, fontWeight: 900, letterSpacing: "-1px", textShadow: TS, textAlign: "center", lineHeight: 1.12 }}>NEXT WEEK<br />INSIDE TABASCO</div>
      <div style={{ color: CREAM, fontSize: 22, letterSpacing: "0.16em", marginTop: 26, textShadow: TS, opacity: subOp }}>SUBSCRIBE · NEW VIDEO EVERY WEEK</div>
      <div style={{ color: SILVER, fontSize: 14, letterSpacing: "0.24em", marginTop: 14, textShadow: TS, opacity: nextOp }}>TOP-VOTED COMMENT BECOMES OUR NEXT VIDEO</div>
    </AbsoluteFill>
  );
};

// ════════════════════════════════════════════════════════════════════
// MASTER TIMELINE — every window tied to coca-cola word_timestamps
// ════════════════════════════════════════════════════════════════════
type Item = { from: number; to: number; node: (s: number) => React.ReactNode };

const ITEMS: Item[] = [
  // ── PART 1 · HOOK (DEA + Federal Register + Secret Formula + Coca Leaves)
  { from: 5.5, to: 11.0, node: (s) => <Stamp s={s} title="DEA EXEMPTION" sub="U.S. GOVERNMENT LICENSE" atS={5.877} outS={11.0} color={COKE_RED} /> },
  { from: 14.6, to: 19.7, node: (s) => <TextHighlight s={s} pre="hidden in plain sight in the" hi="FEDERAL REGISTER" post="" hiS={16.328} hiColor={GOLD} outS={19.7} /> },
  { from: 20.0, to: 24.2, node: (s) => <HookText s={s} text={"THE WORLD'S MOST FAMOUS\nSECRET FORMULA"} atS={20.232} outS={24.2} /> },
  { from: 24.3, to: 28.0, node: (s) => <TextHighlight s={s} pre="what's happening with those" hi="COCA LEAVES" post="" hiS={24.536} hiColor={COKE_RED} outS={28.0} /> },
  { from: 29.2, to: 33.0, node: (s) => <TextHighlight s={s} pre="will change how you see" hi="THAT RED CAN" post="forever" hiS={30.763} hiColor={COKE_RED} outS={33.0} /> },
  { from: 35.1, to: 37.2, node: (s) => <TextHighlight s={s} pre="every product has a" hi="SECRET" post="" hiS={36.430} hiColor={COKE_RED} outS={37.2} /> },

  // ── PART 2 · FACTORY INTRO (Wakefield + scale teaser)
  { from: 37.4, to: 45.0, node: (s) => <LowerThird s={s} name="CCEP WAKEFIELD" sub="GREAT BRITAIN" italic="Europe's largest soft drinks plant by volume" nameS={37.861} subS={38.79} outS={45.0} /> },
  { from: 43.5, to: 49.0, node: (s) => <TextHighlight s={s} pre="this plant ships" hi="MILLIONS" post="of cans · every single day" hiS={43.858} hiColor={COKE_RED} outS={49.0} /> },
  { from: 50.5, to: 56.0, node: (s) => <EquationStack s={s} l1="A SINGLE DROP OF WATER" op="→" l2="A FINISHED CAN OF COKE" l1S={50.947} opS={52.0} l2S={52.749} outS={56.0} /> },

  // ── PART 3 · HISTORY (1886 → Pemberton → 5¢ → Candler → 1915 patent)
  { from: 56.5, to: 62.5, node: (s) => <Stamp s={s} title="MAY 8, 1886" sub="ATLANTA · PHARMACY DEBUT" atS={57.358} outS={62.5} color={GOLD} rotate={-10} /> },
  { from: 59.5, to: 67.5, node: (s) => <LowerThird s={s} name="DR. JOHN PEMBERTON" sub="ATLANTA, GEORGIA" italic="medicinal nerve tonic · Jacobs' Pharmacy" nameS={59.945} subS={61.589} outS={67.5} /> },
  { from: 68.8, to: 73.0, node: (s) => <BigStat s={s} label="FIRST SALE PRICE · 1886" value={0.05} valueS={69.509} decimals={2} prefix="$" sub="per glass · Jacobs' Pharmacy" outS={73.0} /> },
  { from: 72.5, to: 80.0, node: (s) => <LowerThird s={s} name="ASA CANDLER" sub="1899 · FRANCHISE LAUNCH" italic="independent bottlers · global expansion" nameS={73.315} subS={75.778} outS={80.0} /> },
  { from: 88.5, to: 95.0, node: (s) => <Stamp s={s} title="U.S. PATENT 48,160" sub="1915 · CONTOUR BOTTLE" atS={89.406} outS={95.0} color={GOLD} rotate={-6} /> },

  // ── PART 4 · WATER PURIFICATION (Phase 01 · Krones · 0.02 mg/L · 0–4°C)
  { from: 109.5, to: 116.0, node: (s) => <Chapter s={s} num="01" title="WATER PURIFICATION" sub="MUNICIPAL → ULTRA-PURE" numS={109.838} titleS={111.44} outS={116.0} /> },
  { from: 123.7, to: 128.5, node: (s) => <TextHighlight s={s} pre="strips out chlorine through" hi="CARBON BEDS" post="" hiS={124.4} hiColor={GOLD} outS={128.5} /> },
  { from: 134.8, to: 141.8, node: (s) => <Stamp s={s} title="KRONES CONTIFLOW" sub="DEAERATION MIXER" atS={135.099} outS={141.8} color={SILVER} rotate={-5} /> },
  { from: 141.9, to: 148.5, node: (s) => <BigStat s={s} label="DISSOLVED OXYGEN · MAX" value={0.02} valueS={142.417} decimals={2} suffix=" mg/L" sub="parts per billion precision" outS={148.5} /> },
  { from: 155.8, to: 160.2, node: (s) => <BigStat s={s} label="WATER TEMPERATURE" value={4} valueS={156.094} suffix="°C" sub="chilled · feeds carbonator" outS={160.2} /> },

  // ── PART 5 · SYRUP (Phase 02 · Brix · 7X)
  { from: 162.4, to: 169.5, node: (s) => <Chapter s={s} num="02" title="SYRUP PREPARATION" sub="SUGAR / HFCS · BRIX-METERED" numS={162.782} titleS={163.624} outS={169.5} /> },
  { from: 172.6, to: 178.0, node: (s) => <TextHighlight s={s} pre="sweetness measured in" hi="DEGREES BRIX" post="" hiS={173.116} hiColor={GOLD} outS={178.0} /> },
  { from: 179.2, to: 186.0, node: (s) => <Stamp s={s} title="7X FORMULA" sub="TOP-SECRET FLAVOR CONCENTRATE" atS={179.705} outS={186.0} color={COKE_RED} rotate={-8} /> },

  // ── PART 6 · CARBONATION (Phase 03 · 8 bar)
  { from: 205.3, to: 211.0, node: (s) => <Chapter s={s} num="03" title="CARBONATION" sub="HENRY'S LAW · CO₂ INJECTION" numS={205.526} titleS={206.227} outS={211.0} /> },
  { from: 215.3, to: 220.5, node: (s) => <BigStat s={s} label="CO₂ INJECTION PRESSURE" value={8} valueS={215.721} suffix=" BAR" sub="forces gas into solution" outS={220.5} /> },

  // ── PART 7 · SCALE (2.2B + 880 pools)
  { from: 232.4, to: 239.5, node: (s) => <BigStat s={s} label="GLOBAL · DAILY SERVINGS" value={2.2} valueS={232.66} decimals={1} suffix=" BILLION" sub="every single day · worldwide" outS={239.5} /> },
  { from: 239.5, to: 246.5, node: (s) => <BigStat s={s} label="DAILY VOLUME" value={880} valueS={239.727} sub="Olympic-size pools · every day" outS={246.5} /> },

  // ── PART 8 · CONTAINERS (Phase 04 · HEUFT · 84k · 1,400/min · 120k = 33/sec)
  { from: 261.0, to: 267.5, node: (s) => <Chapter s={s} num="04" title="CONTAINERS" sub="PET PREFORMS · STRETCH-BLOW MOULD" numS={261.334} titleS={262.135} outS={267.5} /> },
  { from: 275.9, to: 282.0, node: (s) => <Stamp s={s} title="HEUFT INLINE II" sub="HIGH-SPEED VISION INSPECTOR" atS={276.331} outS={282.0} color={SILVER} rotate={-5} /> },
  { from: 285.5, to: 291.0, node: (s) => <BigStat s={s} label="OPTICAL INSPECTION" value={84000} valueS={285.832} suffix=" /hr" sub="containers scanned per hour" outS={291.0} /> },
  { from: 298.6, to: 304.0, node: (s) => <BigStat s={s} label="LINE SPEED" value={1400} valueS={299.059} suffix=" /min" sub="cans · a blurred silver river" outS={304.0} /> },
  { from: 314.6, to: 323.0, node: (s) => <EquationStack s={s} l1="120,000 CANS / HOUR" op="=" l2="33 CANS · EVERY SECOND" l1S={317.013} opS={320.177} l2S={320.4} outS={323.0} /> },

  // ── PART 9 · QUALITY (every 30 min)
  { from: 352.2, to: 359.0, node: (s) => <BigStat s={s} label="DESTRUCTIVE TESTING" value={30} valueS={353.05} suffix=" MIN" sub="full half-hour quarantine if a sample fails" outS={359.0} /> },

  // ── PART 10 · DARK SIDE (water + plastic)
  { from: 373.4, to: 378.0, node: (s) => <HookText s={s} text="WHAT THEY DON'T FILM" atS={373.834} outS={378.0} /> },
  { from: 384.7, to: 389.3, node: (s) => <EquationStack s={s} l1="1.78 L · WATER" op="→" l2="1 L · COKE" l1S={385.151} opS={386.694} l2S={387.935} outS={389.3} /> },
  { from: 389.5, to: 396.5, node: (s) => <BigStat s={s} label="WATER WITHDRAWAL" value={28} valueS={389.799} suffix="%" sub="from high water-stress regions" outS={396.5} /> },
  { from: 405.4, to: 412.5, node: (s) => <PolluterStamp s={s} atS={405.845} outS={412.5} /> },

  // ── PART 11 · CHEMISTRY (130 years lead-in · Henry's Law on the actual word · 10x atm)
  { from: 424.5, to: 428.6, node: (s) => <TextHighlight s={s} pre="for" hi="130 YEARS" post="most people never notice" hiS={425.133} hiColor={GOLD} outS={428.6} /> },
  { from: 428.7, to: 436.0, node: (s) => <Stamp s={s} title="HENRY'S LAW" sub="THE WHOLE TRICK" atS={429.099} outS={436.0} color={COKE_RED} rotate={-7} /> },
  { from: 449.0, to: 455.5, node: (s) => <BigStat s={s} label="CARBONATION PRESSURE" value={10} valueS={449.409} suffix="×" sub="normal atmosphere" outS={455.5} /> },

  // ── PART 12 · COCA LEAF REVEAL (Stepan · Peru/Bolivia · decocainized · 7X)
  { from: 485.3, to: 494.0, node: (s) => <LowerThird s={s} name="THE STEPAN COMPANY" sub="MAYWOOD, NEW JERSEY" italic="sole DEA-licensed coca leaf importer in the U.S." nameS={485.711} subS={487.053} outS={494.0} /> },
  { from: 490.9, to: 497.5, node: (s) => <TextHighlight s={s} pre="leaves imported from" hi="PERU · BOLIVIA" post="" hiS={491.500} hiColor={COKE_RED} outS={497.5} /> },
  { from: 503.0, to: 511.0, node: (s) => <TextHighlight s={s} pre="what Coca-Cola buys is" hi="DECOCAINIZED" post="coca leaf extract" hiS={503.555} hiColor={GOLD} outS={511.0} /> },
  { from: 513.7, to: 519.5, node: (s) => <Stamp s={s} title="THE 7X FORMULA" sub="LEGALLY PROTECTED INGREDIENT" atS={514.143} outS={519.5} color={COKE_RED} rotate={-9} /> },

  // ── PART 13 · DIET COKE (PKU warning + 21 CFR + PKU)
  { from: 536.3, to: 547.0, node: (s) => <PKUWarning s={s} atS={536.667} outS={547.0} /> },
  { from: 559.5, to: 567.5, node: (s) => <LowerThird s={s} name="PHENYLKETONURIA · PKU" sub="RARE GENETIC DISORDER" italic="aspartame metabolizes to phenylalanine · toxic for PKU" nameS={559.979} subS={561.441} outS={567.5} /> },

  // ── PART 14 · OUTRO (signature hiss + endcard)
  { from: 574.0, to: 583.5, node: (s) => <TextHighlight s={s} pre="that" hi="SIGNATURE HISS" post="· a century of chemistry escaping" hiS={574.777} hiColor={COKE_RED} outS={583.5} /> },
  { from: 634.0, to: 640.4, node: (s) => <EndCard s={s} atS={634.271} outS={640.0} /> },
];

export const CocaColaFullPreview: React.FC = () => {
  return (
    <AbsoluteFill>
      {ITEMS.map((it, i) => (
        <Sequence key={i} from={sToF(it.from)} durationInFrames={Math.max(1, sToF(it.to) - sToF(it.from))} layout="none">
          {it.node(it.from)}
        </Sequence>
      ))}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
